import { useEffect, useContext, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Search,
  RefreshCcw,
  ArrowLeft,
  FileText,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

export default function EmployeeLicenseRenewalListing() {
  const nav = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const finYearId = localStorage.getItem("finYearId") || "";
  const loginBranchId = localStorage.getItem("branchId") || ""; // you said you store after login
  const branchType = localStorage.getItem("branchType") || "HO"; // set this at login; else default

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const screenBadge = useMemo(() => "INFRA SCREEN : C-8", []);

  const [showFilters, setShowFilters] = useState(true);

  // filters (same as ASP)
  const [empName, setEmpName] = useState("");
  const [empCode, setEmpCode] = useState("");
  const [branch, setBranch] = useState("Select");
  const [deptId, setDeptId] = useState("Select");
  const [gradeId, setGradeId] = useState("Select");
  const [desigId, setDesigId] = useState("Select");
  const [pfNonPf, setPfNonPf] = useState("Select");
  const [search, setSearch] = useState("");

  // dropdown data
  const [branchRows, setBranchRows] = useState([]);
  const [deptRows, setDeptRows] = useState([]);
  const [gradeRows, setGradeRows] = useState([]);
  const [desigRows, setDesigRows] = useState([]);

  // autocomplete
  const [nameSug, setNameSug] = useState([]);
  const [codeSug, setCodeSug] = useState([]);
  const nameTimer = useRef(null);
  const codeTimer = useRef(null);

  // list
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const fetchDDL = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    try {
      const [b, d, g, ds] = await Promise.all([
        axios.get(`${BASE_URL}/api/employee-license-renewal/branches`, {
          params: { finYearId: Number(finYearId) },
        }),
        axios.get(`${BASE_URL}/api/employee-license-renewal/departments`, {
          params: { finYearId: Number(finYearId) },
        }),
        axios.get(`${BASE_URL}/api/employee-license-renewal/grades`, {
          params: { finYearId: Number(finYearId) },
        }),
        axios.get(`${BASE_URL}/api/employee-license-renewal/designations`, {
          params: { finYearId: Number(finYearId) },
        }),
      ]);

      setBranchRows(b?.data?.rows || []);
      setDeptRows(d?.data?.rows || []);
      setGradeRows(g?.data?.rows || []);
      setDesigRows(ds?.data?.rows || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to load dropdowns");
    }
  };

  const fetchList = async (p = page, ps = pageSize) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/employee-license-renewal/list`, {
        params: {
          finYearId: Number(finYearId),
          branchType,
          branch,
          deptId,
          gradeId,
          desigId,
          pfNonPf,
          empName: empName.trim(),
          empCode: empCode.trim(),
          search: search.trim(),
          page: p,
          pageSize: ps,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);
        setPageSize(res.data.pageSize || ps);
      } else {
        toast.error(res?.data?.message || "Failed to load list");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDDL();
    // default branch like ASP selected session branch
    if (loginBranchId) setBranch(String(loginBranchId));
    // initial list
    fetchList(1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // pagination change
  useEffect(() => {
    fetchList(page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const onSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchList(1, pageSize);
  };

  const onReset = async () => {
    setEmpName("");
    setEmpCode("");
    setDeptId("Select");
    setGradeId("Select");
    setDesigId("Select");
    setPfNonPf("Select");
    setSearch("");
    setPage(1);
    // keep branch default = login branch (like ASP)
    if (loginBranchId) setBranch(String(loginBranchId));
    await fetchList(1, pageSize);
  };

  const openEntry = (employeeId) => {
    // like ASP: Session["licempid"] = employeeId
    localStorage.setItem("licempid", String(employeeId));
    nav("/payroll/EmployeeLicenseRenewalEntry/entry");
  };

  const openRemarks = (employeeId) => {
    localStorage.setItem("LicEmployeeID", String(employeeId));
    nav("/payroll/LicenseRemarksListing/listing");
  };

  // ----- autocomplete (name)
  const fetchNameSuggestions = async (prefix) => {
    const p = prefix.trim();
    if (!p || p.length < 1) {
      setNameSug([]);
      return;
    }
    try {
      const res = await axios.get(`${BASE_URL}/api/employee-license-renewal/autocomplete/name`, {
        params: { finYearId: Number(finYearId), branchType, branch, prefix: p },
      });
      if (res?.data?.success) setNameSug(res.data.rows || []);
    } catch {
      // silent
    }
  };

  const fetchCodeSuggestions = async (prefix) => {
    const p = prefix.trim();
    if (!p || p.length < 1) {
      setCodeSug([]);
      return;
    }
    try {
      const res = await axios.get(`${BASE_URL}/api/employee-license-renewal/autocomplete/code`, {
        params: { finYearId: Number(finYearId), branchType, branch, prefix: p },
      });
      if (res?.data?.success) setCodeSug(res.data.rows || []);
    } catch {
      // silent
    }
  };

  const onEmpNameChange = (v) => {
    setEmpName(v);
    clearTimeout(nameTimer.current);
    nameTimer.current = setTimeout(() => fetchNameSuggestions(v), 250);
  };

  const onEmpCodeChange = (v) => {
    setEmpCode(v);
    clearTimeout(codeTimer.current);
    codeTimer.current = setTimeout(() => fetchCodeSuggestions(v), 250);
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">EMPLOYEE LICENSE RENEWAL - LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-3">
              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>
              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={() => setShowFilters((v) => !v)} className={ghostBtn}>
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <form onSubmit={onSearch} className={cardBase + " p-6 mb-6"}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="text-lg font-extrabold">Search Criteria</div>

                <div className="flex flex-wrap gap-3">
                  <button type="button" onClick={onReset} className={ghostBtn} disabled={loading}>
                    Clear
                  </button>

                  <button
                    type="button"
                    onClick={() => fetchList(page, pageSize)}
                    className={ghostBtn}
                    disabled={loading}
                  >
                    <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                    Refresh
                  </button>

                  <button type="submit" className={primaryBtn} disabled={loading}>
                    <Search className="w-4 h-4" />
                    Search
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="relative">
                  <label className={labelBase}>Employee Name</label>
                  <input
                    value={empName}
                    onChange={(e) => onEmpNameChange(e.target.value)}
                    className={inputBase + " mt-2"}
                    placeholder="Type employee name..."
                    autoComplete="off"
                  />
                  {nameSug.length > 0 && empName.trim() && (
                    <div className={`absolute z-20 mt-1 w-full rounded-2xl border shadow-lg overflow-hidden ${isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"}`}>
                      {nameSug.slice(0, 10).map((s, i) => (
                        <button
                          type="button"
                          key={i}
                          className={`w-full text-left px-4 py-2 text-sm hover:${isDark ? "bg-gray-800" : "bg-gray-100"}`}
                          onClick={() => {
                            setEmpName(s);
                            setNameSug([]);
                          }}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="relative">
                  <label className={labelBase}>Employee Code</label>
                  <input
                    value={empCode}
                    onChange={(e) => onEmpCodeChange(e.target.value)}
                    className={inputBase + " mt-2"}
                    placeholder="Type employee code..."
                    autoComplete="off"
                  />
                  {codeSug.length > 0 && empCode.trim() && (
                    <div className={`absolute z-20 mt-1 w-full rounded-2xl border shadow-lg overflow-hidden ${isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"}`}>
                      {codeSug.slice(0, 10).map((s, i) => (
                        <button
                          type="button"
                          key={i}
                          className={`w-full text-left px-4 py-2 text-sm hover:${isDark ? "bg-gray-800" : "bg-gray-100"}`}
                          onClick={() => {
                            setEmpCode(s);
                            setCodeSug([]);
                          }}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className={labelBase}>Branch</label>
                  <select value={branch} onChange={(e) => setBranch(e.target.value)} className={inputBase + " mt-2"}>
                    <option value="Select">Select</option>
                    {branchRows.map((b, idx) => {
                      const val = b.fld_Branchid ?? b.branchId ?? b.id ?? b.value;
                      const txt = b.fld_BranchName ?? b.branchName ?? b.text ?? b.name;
                      if (val == null) return null;
                      return (
                        <option key={idx} value={String(val)}>
                          {txt || val}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>Department</label>
                  <select value={deptId} onChange={(e) => setDeptId(e.target.value)} className={inputBase + " mt-2"}>
                    <option value="Select">Select</option>
                    {deptRows.map((d) => (
                      <option key={d.deptId} value={String(d.deptId)}>
                        {d.deptName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>Grade</label>
                  <select value={gradeId} onChange={(e) => setGradeId(e.target.value)} className={inputBase + " mt-2"}>
                    <option value="Select">Select</option>
                    {gradeRows.map((g) => (
                      <option key={g.gradeId} value={String(g.gradeId)}>
                        {g.gradeName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>Designation</label>
                  <select value={desigId} onChange={(e) => setDesigId(e.target.value)} className={inputBase + " mt-2"}>
                    <option value="Select">Select</option>
                    {desigRows.map((d) => (
                      <option key={d.desigId} value={String(d.desigId)}>
                        {d.desigName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>PF / Non PF</label>
                  <select value={pfNonPf} onChange={(e) => setPfNonPf(e.target.value)} className={inputBase + " mt-2"}>
                    <option value="Select">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>

                <div>
                  <label className={labelBase}>Text Search (any column)</label>
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className={inputBase + " mt-2"}
                    placeholder="Search license, branch, designation..."
                  />
                </div>
              </div>
            </form>
          )}

          {/* ==================== RESULTS TABLE ==================== */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              {/* make table wider so columns don’t squeeze */}
              <table className="min-w-[1400px] w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold w-[70px]">S.No</th>
                    <th className="px-4 py-4 text-center font-bold w-[140px]">EMP Code</th>
                    <th className="px-4 py-4 text-center font-bold w-[220px]">EMP Name</th>
                    <th className="px-4 py-4 text-center font-bold w-[200px]">Designation</th>
                    <th className="px-4 py-4 text-center font-bold w-[200px]">Driving License No.</th>
                    <th className="px-4 py-4 text-center font-bold w-[220px]">Driving License Expiry</th>
                    <th className="px-4 py-4 text-center font-bold w-[180px]">Other Expiry</th>
                    <th className="px-4 py-4 text-center font-bold w-[200px]">Branch Name</th>
                    <th className="px-4 py-4 text-center font-bold w-[140px]">Remarks</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 9 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={9} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => {
                      const employeeId = r.fld_employeeid ?? r.fld_Employeeid ?? r.employeeId;
                      const empCodeV = r.fld_employeecode ?? r.fld_Employeecode ?? r.empCode;
                      const empNameV = r.fld_Employee ?? r.fld_employee ?? r.empName;
                      const desigNameV = r.fld_designationname ?? r.desigName;
                      const licNum = r.fld_drivinglicnum ?? "";
                      const licExp = r.fld_drivinglicexpdate ?? "";
                      const otherExp = r.fld_otherexpirydate ?? "";
                      const brName = r.fld_BranchName ?? r.fld_branchname ?? "";

                      return (
                        <tr
                          key={`${employeeId}-${idx}`}
                          className={`${
                            idx % 2 === 0
                              ? isDark ? "bg-gray-800" : "bg-white"
                              : isDark ? "bg-gray-750" : "bg-gray-50"
                          } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                        >
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {(page - 1) * pageSize + idx + 1}
                          </td>

                          <td className="px-4 py-4 text-sm text-center">
                            <button
                              type="button"
                              onClick={() => openEntry(employeeId)}
                              className={`inline-flex items-center gap-1 font-bold underline underline-offset-2 ${
                                isDark ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:text-blue-800"
                              }`}
                              title="Edit / Open Entry"
                            >
                             {empCodeV}
                            </button>
                          </td>

                          <td className={`px-4 py-4 text-sm text-center font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                            {empNameV}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {desigNameV}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {licNum}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {licExp}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {otherExp}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {brName}
                          </td>

                          <td className="px-4 py-4 text-center">
                            <button
                              type="button"
                              onClick={() => openRemarks(employeeId)}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-bold rounded-2xl hover:bg-indigo-700 transform hover:-translate-y-0.5 transition-all shadow"
                            >
                              <FileText className="w-4 h-4" />
                              Remarks
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ==================== PAGINATION ==================== */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <select
                value={pageSize}
                onChange={(e) => {
                  setPage(1);
                  setPageSize(Number(e.target.value));
                }}
                className={inputBase + " !py-2 !rounded-2xl w-[120px]"}
              >
                {[10, 20, 30, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </select>

              <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  <span className="inline-flex items-center gap-2">
                    <ChevronLeft className="w-4 h-4" /> Prev
                  </span>
                </button>

                <div className={`px-4 py-2 text-sm font-bold ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  {page} / {totalPages}
                </div>

                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  <span className="inline-flex items-center gap-2">
                    Next <ChevronRight className="w-4 h-4" />
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
}